import { useCallback, useMemo, useState } from 'react';
import type { ZodType } from 'zod';

import { collectErrors } from '@/lib/validation';

export interface UseFormOptions<T> {
  initialValues: T;
  schema: ZodType;
  onSubmit: (values: T) => void;
  /** Server-side field errors, merged in and cleared as the user edits. */
  serverErrors?: Record<string, string>;
}

export interface UseFormResult<T> {
  values: T;
  /** Only errors the user should see: a field they have touched, or post-submit. */
  errors: Record<string, string>;
  setField: <K extends keyof T>(name: K, value: T[K]) => void;
  /** Marks a field touched so its error can surface. Wire to onBlur. */
  touch: (name: keyof T) => void;
  handleSubmit: (event: { preventDefault: () => void }) => void;
  /** True once the schema passes, for disabling a submit button. */
  isValid: boolean;
  reset: (values?: T) => void;
}

/**
 * Small controlled-form helper: values, per-field validation and error timing.
 *
 * The timing is the point. Validating on every keystroke shouts at someone
 * halfway through typing an email address; validating only on submit makes
 * them hunt for what went wrong. So a field's error appears when they leave it
 * or when they try to submit, and once shown it updates live as they fix it.
 */
export function useForm<T extends Record<string, unknown>>({
  initialValues,
  schema,
  onSubmit,
  serverErrors,
}: UseFormOptions<T>): UseFormResult<T> {
  const [values, setValues] = useState<T>(initialValues);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);

  /**
   * Fields the user has actually typed in.
   *
   * Blur alone is not enough to justify an error: `autoFocus` puts the caret in
   * the first field, so tabbing or clicking away would otherwise paint it red
   * with "this is required" before the user has done anything wrong. A field
   * has to have been edited - or the form submitted - before it complains.
   */
  const [dirty, setDirty] = useState<Record<string, boolean>>({});

  /**
   * Fields edited since the server last replied. Their stale server error is
   * suppressed, so "username already taken" clears the moment it is changed.
   *
   * State rather than a ref because it feeds the rendered error list, and a
   * ref read during render would not trigger the re-render that hides it.
   */
  const [editedSinceSubmit, setEditedSinceSubmit] = useState<Record<string, boolean>>({});

  const validation = useMemo(() => collectErrors(schema, values), [schema, values]);

  const errors = useMemo(() => {
    const visible: Record<string, string> = {};

    for (const [field, message] of Object.entries(validation.errors)) {
      if (submitted || (touched[field] && dirty[field])) {
        visible[field] = message;
      }
    }

    /* Server errors win: they know things the client cannot, such as whether a
       username is already taken. */
    for (const [field, message] of Object.entries(serverErrors ?? {})) {
      if (!editedSinceSubmit[field]) {
        visible[field] = message;
      }
    }

    return visible;
  }, [validation.errors, touched, dirty, submitted, serverErrors, editedSinceSubmit]);

  const setField = useCallback<UseFormResult<T>['setField']>((name, value) => {
    const key = String(name);

    setEditedSinceSubmit((previous) => (previous[key] ? previous : { ...previous, [key]: true }));
    setDirty((previous) => (previous[key] ? previous : { ...previous, [key]: true }));
    setValues((previous) => ({ ...previous, [name]: value }));
  }, []);

  const touch = useCallback<UseFormResult<T>['touch']>((name) => {
    const key = String(name);

    setTouched((previous) => (previous[key] ? previous : { ...previous, [key]: true }));
  }, []);

  const handleSubmit = useCallback<UseFormResult<T>['handleSubmit']>(
    (event) => {
      event.preventDefault();
      setSubmitted(true);

      const result = collectErrors(schema, values);

      if (!result.valid) {
        /* Put the caret on the first problem rather than leaving the user to
           scan a long form for the red one. */
        const firstField = Object.keys(result.errors)[0];
        if (firstField) {
          const element = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
          element?.focus();
          element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        return;
      }

      setEditedSinceSubmit({});
      onSubmit(values);
    },
    [schema, values, onSubmit],
  );

  const reset = useCallback<UseFormResult<T>['reset']>(
    (next) => {
      setValues(next ?? initialValues);
      setTouched({});
      setDirty({});
      setSubmitted(false);
      setEditedSinceSubmit({});
    },
    [initialValues],
  );

  return { values, errors, setField, touch, handleSubmit, isValid: validation.valid, reset };
}
